import axios from "axios";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import geografiaFunciones from "../function/geografia.Funciones";

const MODULO = "Grupos";

const normalizarTexto = (texto = "") =>
  texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const RegistrarGrupos = ({ actualizarGrupos }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const isEdit = useMemo(() => Boolean(id), [id]);

  const [fichaGrupo, setFichaGrupo] = useState("");

  const [programas, setProgramas] = useState([]);
  const [codPrograma, setCodPrograma] = useState("");
  const [programaSeleccionado, setProgramaSeleccionado] = useState(null);

  const [centros, setCentros] = useState([]);
  const [codCentro, setCodCentro] = useState("");
  const [centroSeleccionado, setCentroSeleccionado] = useState(null);

  const [finiciolectivaGrupo, setFiniciolectivaGrupo] = useState("");
  const [ffinlectivaGrupo, setFfinlectivaGrupo] = useState("");
  const [finicioproductivaGrupo, setFinicioproductivaGrupo] = useState("");
  const [ffinproductivaGrupo, setFfinproductivaGrupo] = useState("");

  const [paises, setPaises] = useState([]);
  const [estados, setEstados] = useState([]);
  const [ciudades, setCiudades] = useState([]);

  const [idPais, setIdPais] = useState("");
  const [idEstado, setIdEstado] = useState("");
  const [idCiudad, setIdCiudad] = useState("");

  const [paisSeleccionado, setPaisSeleccionado] = useState(null);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState(null);
  const [ciudadSeleccionada, setCiudadSeleccionada] = useState(null);

  const [estadoGrupo, setEstadoGrupo] = useState("");
  const [observacionGrupo, setObservaciongrupo] = useState("");
  const [cantCuposGrupo, setCantCuposGrupo] = useState("");

  const authConfig = () => {
    const token = localStorage.getItem("token");

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const getModuloGrupos = () => {
    try {
      const permisosStorage = localStorage.getItem("permisos");
      const usuarioStorage = localStorage.getItem("usuario");
      const userStorage = localStorage.getItem("user");
      const usuariosStorage = localStorage.getItem("usuarios");

      const permisosData = permisosStorage
        ? JSON.parse(permisosStorage)
        : usuarioStorage
        ? JSON.parse(usuarioStorage)
        : userStorage
        ? JSON.parse(userStorage)
        : usuariosStorage
        ? JSON.parse(usuariosStorage)
        : {};

      const modulos = Array.isArray(permisosData)
        ? permisosData
        : permisosData?.modulos ||
          permisosData?.permisos ||
          permisosData?.usuario?.modulos ||
          permisosData?.usuario?.permisos ||
          permisosData?.user?.modulos ||
          permisosData?.user?.permisos ||
          [];

      const moduloBuscado = normalizarTexto(MODULO);

      return (
        modulos.find((modulo) => {
          const nombre =
            modulo?.nombreModulo || modulo?.modulo || modulo?.nombre || "";

          return normalizarTexto(nombre) === moduloBuscado;
        }) || {}
      );
    } catch (error) {
      console.error("Error leyendo permisos:", error);
      return {};
    }
  };

  const moduloGrupos = useMemo(() => getModuloGrupos(), []);

  const puedeCrear =
    moduloGrupos?.puedeCrear === true ||
    moduloGrupos?.crear === true ||
    moduloGrupos?.create === true;

  const puedeActualizar =
    moduloGrupos?.puedeActualizar === true ||
    moduloGrupos?.actualizar === true ||
    moduloGrupos?.editar === true ||
    moduloGrupos?.update === true;

  const puedeGuardarFormulario = isEdit ? puedeActualizar : puedeCrear;

  const manejarErrorSesion = useCallback(
    (error) => {
      if (error?.response?.status === 401) {
        Swal.fire({
          icon: "warning",
          title: "Sesión expirada",
          text: "Por favor inicia sesión nuevamente.",
          confirmButtonText: "Aceptar",
        }).then(() => {
          localStorage.removeItem("token");
          navigate("/", { replace: true });
        });

        return true;
      }

      return false;
    },
    [navigate]
  );

  const selectStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, width: "100%" }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#86b7fe" : base.borderColor,
        boxShadow: state.isFocused
          ? "0 0 0 .25rem rgba(13,110,253,.15)"
          : "none",
      }),
    }),
    []
  );

  const opcionesPaises = useMemo(
    () => paises.map((p) => ({ value: String(p.idPais), label: p.nombrePais })),
    [paises]
  );

  const opcionesEstados = useMemo(
    () =>
      estados.map((e) => ({
        value: String(e.idEstado),
        label: e.nombreEstado,
      })),
    [estados]
  );

  const opcionesCiudades = useMemo(
    () =>
      ciudades.map((c) => ({
        value: String(c.idCiudad),
        label: c.nombreCiudad,
      })),
    [ciudades]
  );

  const opcionesPrograma = useMemo(() => {
    return programas
      .slice()
      .sort((a, b) =>
        (a?.nombrePrograma || "").localeCompare(b?.nombrePrograma || "")
      )
      .map((programa) => ({
        value: String(programa.codPrograma),
        label: `${programa.codPrograma} - ${
          (programa.nombrePrograma || "").length > 60
            ? (programa.nombrePrograma || "").substring(0, 60) + "..."
            : programa.nombrePrograma || ""
        }`,
      }));
  }, [programas]);

  const opcionesCentro = useMemo(() => {
    return centros
      .slice()
      .sort((a, b) =>
        (a?.nombreCentro || "").localeCompare(b?.nombreCentro || "")
      )
      .map((centro) => ({
        value: String(centro.codCentro),
        label: `${centro.codCentro} - ${
          (centro.nombreCentro || "").length > 60
            ? (centro.nombreCentro || "").substring(0, 60) + "..."
            : centro.nombreCentro || ""
        }`,
      }));
  }, [centros]);

  const cleanData = useCallback(() => {
    setFichaGrupo("");
    setCodPrograma("");
    setProgramaSeleccionado(null);
    setCodCentro("");
    setCentroSeleccionado(null);
    setFiniciolectivaGrupo("");
    setFfinlectivaGrupo("");
    setFinicioproductivaGrupo("");
    setFfinproductivaGrupo("");
    setIdPais("");
    setIdEstado("");
    setIdCiudad("");
    setPaisSeleccionado(null);
    setEstadoSeleccionado(null);
    setCiudadSeleccionada(null);
    setEstados([]);
    setCiudades([]);
    setEstadoGrupo("");
    setObservaciongrupo("");
    setCantCuposGrupo("");
  }, []);

  useEffect(() => {
    const cargarPaises = async () => {
      try {
        const paisesData = await geografiaFunciones.obtenerPaises();

        const paisesUnicos = (paisesData || []).filter(
          (pais, index, self) =>
            index === self.findIndex((p) => p.idPais === pais.idPais)
        );

        setPaises(paisesUnicos);
      } catch (error) {
        console.error("Error al cargar países:", error);
      }
    };

    cargarPaises();
  }, []);

  useEffect(() => {
    const cargarProgramas = async () => {
      try {
        const { data } = await axios.get("/api/programas/listall/", authConfig());

        const lista = Array.isArray(data?.programas) ? data.programas : [];

        setProgramas(lista);
      } catch (error) {
        console.error("Error cargando programas:", error);

        if (manejarErrorSesion(error)) return;

        setProgramas([]);

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudieron cargar los programas.",
          "error"
        );
      }
    };

    cargarProgramas();
  }, [manejarErrorSesion]);

  useEffect(() => {
    const cargarCentros = async () => {
      try {
        const { data } = await axios.get(
          "/api/centrosdeformacion/listall/",
          authConfig()
        );

        const lista = Array.isArray(data?.centros) ? data.centros : [];

        setCentros(lista);
      } catch (error) {
        console.error("Error cargando centros:", error);

        if (manejarErrorSesion(error)) return;

        setCentros([]);

        Swal.fire(
          "Error",
          error.response?.data?.message || "No se pudieron cargar los centros.",
          "error"
        );
      }
    };

    cargarCentros();
  }, [manejarErrorSesion]);

  const handlePaisChange = async (selected) => {
    setPaisSeleccionado(selected);
    setIdPais(selected?.value || "");

    setEstadoSeleccionado(null);
    setCiudadSeleccionada(null);
    setIdEstado("");
    setIdCiudad("");
    setEstados([]);
    setCiudades([]);

    if (selected?.value) {
      const data = await geografiaFunciones.obtenerEstadosPorPais(
        selected.value
      );

      const unicos = Array.from(
        new Map((data || []).map((e) => [e.idEstado, e])).values()
      );

      setEstados(unicos);
    }
  };

  const handleEstadoChange = async (selected) => {
    setEstadoSeleccionado(selected);
    setIdEstado(selected?.value || "");

    setCiudadSeleccionada(null);
    setIdCiudad("");
    setCiudades([]);

    if (selected?.value) {
      const data = await geografiaFunciones.obtenerCiudadesPorEstado(
        selected.value
      );

      const unicos = Array.from(
        new Map((data || []).map((c) => [c.idCiudad, c])).values()
      );

      setCiudades(unicos);
    }
  };

  const handleCiudadChange = (selected) => {
    setCiudadSeleccionada(selected);
    setIdCiudad(selected?.value || "");
  };

  const handleProgramaChange = (selectedOption) => {
    setProgramaSeleccionado(selectedOption || null);
    setCodPrograma(selectedOption?.value || "");
  };

  const handleCentroChange = (selectedOption) => {
    setCentroSeleccionado(selectedOption || null);
    setCodCentro(selectedOption?.value || "");
  };

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);

        const res = await axios.get(`/api/grupos/listid/${id}`, authConfig());

        if (!res.data?.ok) return;

        const grupo = res.data.grupo;

        const toDate = (d) =>
          d ? new Date(d).toISOString().split("T")[0] : "";

        setFichaGrupo(grupo.fichaGrupo || "");
        setCodPrograma(String(grupo.codPrograma || ""));
        setProgramaSeleccionado(null);
        setCodCentro(String(grupo.codCentro || ""));
        setCentroSeleccionado(null);

        setFiniciolectivaGrupo(toDate(grupo.finiciolectivaGrupo));
        setFfinlectivaGrupo(toDate(grupo.ffinlectivaGrupo));
        setFinicioproductivaGrupo(toDate(grupo.finicioproductivaGrupo));
        setFfinproductivaGrupo(toDate(grupo.ffinproductivaGrupo));

        setEstadoGrupo(grupo.estadoGrupo || "");
        setObservaciongrupo(grupo.observacionGrupo || "");
        setCantCuposGrupo(grupo.cantCuposGrupo || "");

        const paisesData = await geografiaFunciones.obtenerPaises();
        setPaises(paisesData || []);

        const estadosData = grupo.idPais
          ? await geografiaFunciones.obtenerEstadosPorPais(grupo.idPais)
          : [];

        const estadosUnicos = Array.from(
          new Map((estadosData || []).map((e) => [e.idEstado, e])).values()
        );

        setEstados(estadosUnicos);

        const ciudadesData = grupo.idEstado
          ? await geografiaFunciones.obtenerCiudadesPorEstado(grupo.idEstado)
          : [];

        const ciudadesUnicas = Array.from(
          new Map((ciudadesData || []).map((c) => [c.idCiudad, c])).values()
        );

        setCiudades(ciudadesUnicas);

        setIdPais(String(grupo.idPais || ""));
        setIdEstado(String(grupo.idEstado || ""));
        setIdCiudad(String(grupo.idCiudad || ""));

        const paisObj = (paisesData || []).find(
          (p) => String(p.idPais) === String(grupo.idPais)
        );

        setPaisSeleccionado(
          paisObj
            ? { value: String(paisObj.idPais), label: paisObj.nombrePais }
            : null
        );

        const estadoObj = estadosUnicos.find(
          (e) => String(e.idEstado) === String(grupo.idEstado)
        );

        setEstadoSeleccionado(
          estadoObj
            ? {
                value: String(estadoObj.idEstado),
                label: estadoObj.nombreEstado,
              }
            : null
        );

        const ciudadObj = ciudadesUnicas.find(
          (c) => String(c.idCiudad) === String(grupo.idCiudad)
        );

        setCiudadSeleccionada(
          ciudadObj
            ? {
                value: String(ciudadObj.idCiudad),
                label: ciudadObj.nombreCiudad,
              }
            : null
        );
      } catch (error) {
        console.error("Error al cargar grupo:", error);

        if (manejarErrorSesion(error)) return;

        Swal.fire("Error", "No se pudo cargar el grupo", "error");
      } finally {
        setLoading(false);
      }
    };

    if (id) getData();
  }, [id, manejarErrorSesion]);

  useEffect(() => {
    if (codPrograma && opcionesPrograma.length) {
      const found =
        opcionesPrograma.find(
          (op) => String(op.value) === String(codPrograma)
        ) || null;

      setProgramaSeleccionado(found);
    }
  }, [codPrograma, opcionesPrograma]);

  useEffect(() => {
    if (codCentro && opcionesCentro.length) {
      const found =
        opcionesCentro.find((op) => String(op.value) === String(codCentro)) ||
        null;

      setCentroSeleccionado(found);
    }
  }, [codCentro, opcionesCentro]);

  const saveGrupo = async () => {
    if (!puedeCrear) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para registrar grupos.",
        "warning"
      );

      return;
    }

    try {
      setLoading(true);

      const newGrupo = {
        fichaGrupo,
        codPrograma,
        codCentro,
        finiciolectivaGrupo,
        ffinlectivaGrupo,
        finicioproductivaGrupo,
        ffinproductivaGrupo,
        idPais,
        idEstado,
        idCiudad,
        estadoGrupo,
        observacionGrupo,
        cantCuposGrupo,
      };

      const { data } = await axios.post(
        "/api/grupos/add",
        newGrupo,
        authConfig()
      );

      if (!data?.ok) {
        return Swal.fire(
          "Error",
          data?.message || "No se pudo registrar.",
          "error"
        );
      }

      await Swal.fire({
        icon: "success",
        title: "El Grupo ha sido registrado con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      actualizarGrupos?.();
      cleanData();
      navigate("/institucional/grupos");
    } catch (error) {
      console.error("Error en saveGrupo:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message || "Hubo un problema con la solicitud.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const updateGrupo = async () => {
    if (!puedeActualizar) {
      Swal.fire(
        "Sin permisos",
        "No tiene permisos para actualizar grupos.",
        "warning"
      );

      return;
    }

    try {
      setLoading(true);

      const updatedGrupo = {
        fichaGrupo,
        codPrograma,
        codCentro,
        finiciolectivaGrupo,
        ffinlectivaGrupo,
        finicioproductivaGrupo,
        ffinproductivaGrupo,
        idPais,
        idEstado,
        idCiudad,
        estadoGrupo,
        observacionGrupo,
        cantCuposGrupo,
      };

      const { data } = await axios.put(
        `/api/grupos/update/${id}`,
        updatedGrupo,
        authConfig()
      );

      if (!data?.ok) {
        return Swal.fire(
          "Error",
          data?.message || "No se pudo actualizar.",
          "error"
        );
      }

      await Swal.fire({
        icon: "success",
        title: "El Grupo ha sido actualizado con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      actualizarGrupos?.();
      cleanData();
      navigate("/institucional/grupos");
    } catch (error) {
      console.error("Error en updateGrupo:", error);

      if (manejarErrorSesion(error)) return;

      Swal.fire(
        "Error",
        error.response?.data?.message ||
          "Hubo un problema con la solicitud de actualización.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!fichaGrupo || !codPrograma || !codCentro) {
      Swal.fire(
        "Atención",
        "Ficha, programa y centro de formación son obligatorios.",
        "warning"
      );

      return;
    }

    isEdit ? await updateGrupo() : await saveGrupo();
  };

  const handleCancel = () => {
    cleanData();
    navigate("/institucional/grupos/");
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Grupo" : "Registrar Grupo"}
                </h5>

                <small className="text-secondary">
                  Completa la información del grupo (ficha, programa, centro,
                  fechas y ubicación).
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate("/institucional/grupos")}
                  disabled={loading}
                  title="Volver"
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form
              id="form-grupo"
              onSubmit={actions}
              className="needs-validation"
              noValidate
            >
              <div className="row g-3">
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Ficha</label>

                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: 2750123"
                    value={fichaGrupo}
                    onChange={(e) => setFichaGrupo(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Programa</label>

                  <Select
                    options={opcionesPrograma}
                    value={programaSeleccionado}
                    onChange={handleProgramaChange}
                    placeholder="Seleccione un programa"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isDisabled={loading}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    Centro de Formación
                  </label>

                  <Select
                    options={opcionesCentro}
                    value={centroSeleccionado}
                    onChange={handleCentroChange}
                    placeholder="Seleccione un centro"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isDisabled={loading}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    Inicio Lectiva
                  </label>

                  <input
                    type="date"
                    className="form-control"
                    value={finiciolectivaGrupo}
                    onChange={(e) => setFiniciolectivaGrupo(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Fin Lectiva</label>

                  <input
                    type="date"
                    className="form-control"
                    value={ffinlectivaGrupo}
                    onChange={(e) => setFfinlectivaGrupo(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    Inicio Productiva
                  </label>

                  <input
                    type="date"
                    className="form-control"
                    value={finicioproductivaGrupo}
                    onChange={(e) => setFinicioproductivaGrupo(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    Fin Productiva
                  </label>

                  <input
                    type="date"
                    className="form-control"
                    value={ffinproductivaGrupo}
                    onChange={(e) => setFfinproductivaGrupo(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">País</label>

                  <Select
                    options={opcionesPaises}
                    value={paisSeleccionado}
                    onChange={handlePaisChange}
                    placeholder="Seleccione un país"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isDisabled={loading}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">
                    Estado/Departamento
                  </label>

                  <Select
                    options={opcionesEstados}
                    value={estadoSeleccionado}
                    onChange={handleEstadoChange}
                    placeholder="Seleccione un estado"
                    isClearable
                    isDisabled={loading || !opcionesEstados.length}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Ciudad</label>

                  <Select
                    options={opcionesCiudades}
                    value={ciudadSeleccionada}
                    onChange={handleCiudadChange}
                    placeholder="Seleccione una ciudad"
                    isClearable
                    isDisabled={loading || !opcionesCiudades.length}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">
                    Cupos Solicitados
                  </label>

                  <select
                    className="form-select"
                    required
                    value={cantCuposGrupo}
                    onChange={(e) => setCantCuposGrupo(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Seleccione…</option>

                    {Array.from({ length: 50 }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Estado</label>

                  <select
                    className="form-select"
                    value={estadoGrupo}
                    onChange={(e) => setEstadoGrupo(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Seleccione…</option>
                    <option value="ACTIVO">ACTIVO</option>
                    <option value="INACTIVO">INACTIVO</option>
                  </select>
                </div>

                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">
                    {estadoGrupo === ""
                      ? "Motivo del estado"
                      : estadoGrupo === "ACTIVO"
                      ? "Motivo de apertura"
                      : "Motivo de cierre"}
                  </label>

                  <select
                    className="form-select"
                    onChange={(e) => setObservaciongrupo(e.target.value)}
                    value={observacionGrupo}
                    disabled={loading || !estadoGrupo}
                  >
                    <option value="">
                      {estadoGrupo
                        ? "Seleccione un motivo…"
                        : "Seleccione un estado primero…"}
                    </option>

                    {estadoGrupo === "ACTIVO" && (
                      <>
                        <option value="Inicio_programa">Inicio del programa</option>
                        <option value="Reprogramacion">Reprogramación del grupo</option>
                        <option value="Apertura_administrativa">
                          Apertura administrativa
                        </option>
                        <option value="Cambio_modalidad">Cambio de modalidad</option>
                        <option value="Creacion_nuevo_grupo">
                          Creación de un nuevo grupo
                        </option>
                        <option value="Demanda_aprendices">
                          Alta demanda de aprendices
                        </option>
                        <option value="Aumento_cupos">Aumento de cupos</option>
                        <option value="Fusion_grupos">Fusión de grupos</option>
                      </>
                    )}

                    {estadoGrupo === "INACTIVO" && (
                      <>
                        <option value="Finalizacion_programa">
                          Finalización normal del programa
                        </option>
                        <option value="Baja_desercion">
                          Baja por alta deserción
                        </option>
                        <option value="Reprogramacion">Reprogramación del grupo</option>
                        <option value="Cierre_administrativo">
                          Cierre administrativo
                        </option>
                        <option value="Fuerza_mayor">
                          Suspensión por fuerza mayor
                        </option>
                        <option value="Cambio_programa">Cambio de programa</option>
                        <option value="Fusion_fichas">Fusión de fichas</option>
                        <option value="Incumplimiento_requisitos">
                          Incumplimiento de requisitos mínimos
                        </option>
                        <option value="Incumplimiento_instructor">
                          Cierre por incumplimiento del instructor
                        </option>
                        <option value="Renuncia_instructor">
                          Renuncia del instructor
                        </option>
                        <option value="Cambio_modalidad">
                          Cambio de modalidad
                        </option>
                        <option value="Problemas_logisticos">
                          Problemas logísticos
                        </option>
                        <option value="Reubicacion">Reubicación del grupo</option>
                        <option value="Problemas_presupuesto">
                          Problemas presupuestarios
                        </option>
                        <option value="Solicitud_aprendices">
                          Solicitud de cierre por parte de los aprendices
                        </option>
                        <option value="Cierre_temporal">Cierre temporal</option>
                        <option value="Terminacion_contrato">
                          Terminación por incumplimiento del contrato
                        </option>
                        <option value="Evaluacion_baja">
                          Cierre por baja evaluación institucional
                        </option>
                        <option value="Conflictos_internos">
                          Cierre por conflictos internos
                        </option>
                      </>
                    )}
                  </select>
                </div>

                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    {puedeGuardarFormulario && (
                      <button
                        type="submit"
                        form="form-grupo"
                        className="btn btn-dark"
                        disabled={loading}
                        title={isEdit ? "Actualizar" : "Guardar"}
                      >
                        <i
                          className={`fa-solid ${
                            loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                          } me-2`}
                        ></i>

                        {loading
                          ? isEdit
                            ? "Actualizando…"
                            : "Guardando…"
                          : isEdit
                          ? "Actualizar"
                          : "Guardar"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>

            {!puedeGuardarFormulario && (
              <div className="alert alert-warning mt-3 mb-0">
                <i className="fa-solid fa-lock me-2"></i>
                No tienes permiso para {isEdit ? "actualizar" : "crear"} grupos.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarGrupos;